/**
 * Pure PHI/PII scrubber — no vendor SDK import, so it can be shared by the
 * web provider's `beforeSend` hook and by `@galapagos/telemetry`'s standard
 * redactor without pulling `@datadog/browser-logs` into a Node consumer's
 * module graph. Extracted from the web provider (behaviour unchanged).
 */

import { matchRouteTemplate, type RouteTemplateIndex } from './route-template-match';

export const REDACTED = '[REDACTED]';

/**
 * Object keys whose values may carry PHI/PII. Matched case-insensitively
 * against keys anywhere in a payload; matching values are redacted. Substring
 * matches are intentional for most terms (e.g. `memberId`, `homeAddress`), but
 * the `name` clause is anchored so benign keys like `tableName`, `fileName`,
 * `eventName`, `functionName`, `schemaName` are NOT redacted.
 */
export const PHI_KEY_PATTERN =
  /(email|phone|telephone|mobile|address|dob|dateofbirth|ssn|nationalid|memberid|member_id|identityid|identity_id|patient|prescription|medication|insurance|policy|postcode|zip|password|secret|token|authorization|\bname\b|(?:first|last|full|middle|maiden|user|display|patient|member|nick|screen|profile|account)name\b)/i;

/**
 * Keys that substring-match {@link PHI_KEY_PATTERN} but carry only closed,
 * non-PII enums (never an address). `email_match` is `true | false | unknown`.
 */
export const PHI_KEY_SCRUB_EXCEPTIONS = new Set<string>(['email_match', 'member_call_phase']);

function shouldScrubKey(key: string, pattern: RegExp): boolean {
  return pattern.test(key) && !PHI_KEY_SCRUB_EXCEPTIONS.has(key);
}

/**
 * Free-text slots that carry unstructured user content (`note`, `body`,
 * `message`, bare `member`, `tel`) which the key-anchored {@link PHI_KEY_PATTERN}
 * doesn't already cover. Kept here — not in `@galapagos/telemetry` — so the
 * standard-mode chokepoint is a single pattern usable by every SDK egress hook
 * (the server provider can't import the downstream telemetry package).
 */
const EXTRA_FREETEXT_KEYS =
  /(\bnotes?\b|\bbody\b|\bmessage\b|\bdetails?\b|\bstack\b|\btel\b|member|patient)/i;

/**
 * The standard-mode redaction pattern (ADR-069 Rule 5) — {@link PHI_KEY_PATTERN}
 * widened with the free-text slots. This is the single denylist every egress
 * hook scrubs with; `@galapagos/telemetry`'s `redact()` re-exports it.
 */
export const STANDARD_REDACT_PATTERN = new RegExp(
  `${PHI_KEY_PATTERN.source}|${EXTRA_FREETEXT_KEYS.source}`,
  'i',
);

function objectEntries(value: object): Array<readonly [string, unknown]> {
  return Object.keys(value).map((key) => {
    const entryValue: unknown = Reflect.get(value, key);
    return [key, entryValue] as const;
  });
}

function isRecord(value: object): value is Record<string, unknown> {
  return !Array.isArray(value);
}

function scrubObject(
  value: object,
  seen: WeakSet<object>,
  pattern: RegExp,
): Record<string, unknown> | undefined {
  if (seen.has(value)) return undefined;
  seen.add(value);

  const out: Record<string, unknown> = {};
  for (const [key, entryValue] of objectEntries(value)) {
    out[key] = shouldScrubKey(key, pattern) ? REDACTED : scrubPhi(entryValue, seen, pattern);
  }
  return out;
}

/** Scrub an object while retaining a record-shaped result for SDK adapters. */
export function scrubPhiObject(
  value: object,
  seen: WeakSet<object> = new WeakSet(),
  pattern: RegExp = PHI_KEY_PATTERN,
): Record<string, unknown> {
  return scrubObject(value, seen, pattern) ?? {};
}

/** Scrub string-valued trace attributes without widening their value type. */
export function scrubPhiStrings(
  value: Record<string, string>,
  pattern: RegExp = PHI_KEY_PATTERN,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, entryValue] of Object.entries(value)) {
    out[key] = shouldScrubKey(key, pattern) ? REDACTED : entryValue;
  }
  return out;
}

/**
 * Recursively redact PHI-shaped values from an arbitrary payload. Cycles are
 * broken via a WeakSet. `pattern` defaults to {@link PHI_KEY_PATTERN}; callers
 * (e.g. the telemetry standard redactor) may pass a widened pattern.
 */
export function scrubPhi(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
  pattern: RegExp = PHI_KEY_PATTERN,
): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (seen.has(value)) return undefined;

  if (Array.isArray(value)) {
    seen.add(value);
    return value.map((entry) => scrubPhi(entry, seen, pattern));
  }

  return scrubObject(value, seen, pattern);
}

/**
 * Reduce arbitrary analytics properties to RUM-safe action attributes: keep only
 * top-level safe scalars (string / number / boolean) and PHI-scrub them; drop any
 * nested object/array wholesale.
 *
 * RUM custom-action attributes must be flat safe scalars — a nested payload (e.g.
 * a PostHog `screen_view`'s `params` object, which can carry free-text values like
 * `debugText`) is exactly the free-text / high-cardinality shape ADR-069 forbids
 * from telemetry, and key-based scrubbing alone doesn't catch it. The composite
 * fans generic product analytics into RUM, so this dropper runs on the RUM
 * `track` path before `addAction`. This is the standard-mode dropper; a future
 * strict mode tightens it to a default-deny safe-key allowlist.
 */
export function safeRumAttributes(
  properties?: Record<string, unknown>,
  pattern: RegExp = STANDARD_REDACT_PATTERN,
): Record<string, unknown> | undefined {
  if (!properties) return undefined;
  const flat: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(properties)) {
    const t = typeof val;
    // Keep only safe scalars; drop nested objects/arrays, functions, null/undefined.
    if (t === 'string' || t === 'number' || t === 'boolean') {
      flat[key] = val;
    }
  }
  return scrubPhiObject(flat, new WeakSet(), pattern);
}

// ---------------------------------------------------------------------------
// URL sanitisation (ADR-069 Rule 1)
// ---------------------------------------------------------------------------

/**
 * `scrubPhi` above redacts by object *key*. That is blind to an identifier
 * embedded in a string *value* — in a RUM resource event the key is `url` and
 * the member id sits inside the URL, so the key-based pass never matches it.
 * The helpers below close that gap by reducing a URL to its route template, and
 * destroying anything they cannot positively recognise.
 */

/** Replaces a whole path/query that could not be positively recognised. */
export const REDACTED_URL = '[REDACTED_URL]';

/**
 * Replaces a redacted query-parameter value. Deliberately URL-safe and
 * unbracketed: query strings are rebuilt through `URLSearchParams`, which would
 * percent-encode `[` and `]` into `%5B…%5D` and make every redacted parameter
 * harder to read in Datadog for no benefit.
 */
export const REDACTED_QUERY = 'REDACTED';

/**
 * Query parameters whose values may survive. An allowlist, deliberately: a
 * denylist over {@link PHI_KEY_PATTERN} is fail-open here, because
 * `conversationId`, `assessmentId`, `sub`, `userId`, `ref` and `id` do not match
 * that pattern — and those are exactly the identifier names in play.
 */
export const SAFE_QUERY_PARAMS: ReadonlySet<string> = new Set([
  'cursor',
  'limit',
  'offset',
  'page',
  'sort',
  'order',
  'locale',
  'tab',
]);

/**
 * Short, unsigned counts — capped at five digits so the value can never collide
 * with the six-digit run the identifier heuristic treats as an id.
 */
const NUMERIC_VALUE = /^\d{1,5}$/;

/**
 * Parameters whose VALUE may be kept. Deliberately only the numeric ones.
 *
 * A word shape is not a value allowlist: `tab=Jane_Doe`, `sort=Jane_Doe` and
 * `order=SmithJohn` all satisfied it, and a person's name is exactly the PHI
 * this change exists to keep out of telemetry — no digit for an identifier
 * heuristic to catch. Closing the NAME set left the VALUE still shape-tested,
 * which is the same mistake one level down.
 *
 * So enum-ish parameters keep their name and lose their value. The name already
 * carries the diagnostic signal — that a request was tabbed, sorted, or
 * localised — while the value is caller-controlled and cannot be vouched for by
 * any rule this package can express.
 *
 * Per-parameter value validators.
 *
 * A name allowlist alone is not enough, and neither is one generic shape test:
 * `cursor=member-123` and `tab=MEMBER-1` are exactly the short, non-UUID,
 * non-numeric, non-long-hex identifiers this whole change exists to stop, and a
 * generic test admits them. Each parameter therefore states the value shape it
 * actually expects, and anything else is redacted.
 *
 * `cursor` has no validator deliberately: a pagination cursor is opaque and may
 * encode an identifier, so its value can never be vouched for. The parameter
 * name survives — knowing a request was paginated is useful — and only the value
 * is replaced.
 */
const SAFE_QUERY_VALUE: ReadonlyMap<string, RegExp> = new Map([
  ['limit', NUMERIC_VALUE],
  ['offset', NUMERIC_VALUE],
  ['page', NUMERIC_VALUE],
]);

/**
 * How strongly a URL is treated. This decides *how*, never *whether*: every
 * URL-bearing value is treated, and an unrecognised field path gets
 * `destructive` — so a field added by a future SDK version is destroyed rather
 * than leaked.
 */
export type UrlClass = 'api' | 'page' | 'asset' | 'destructive';

/** Prepared template lookups, built once at provider construction. */
export interface UrlTemplateSets {
  readonly api?: RouteTemplateIndex;
  readonly page?: RouteTemplateIndex;
  /**
   * Origins whose asset paths may be retained — in practice the app's own bundle
   * origin. Anything else is destroyed.
   *
   * Retaining an asset path is only safe where the path cannot be member-keyed,
   * and that is a property of the *origin*, not of the string: Atlas stores chat
   * uploads under `${memberId}/${uuid-or-filename}` and serves them from a media
   * origin, so a shape rule would have to catch both a short id like
   * `member-123` and a user-supplied filename carrying a person's name. Neither
   * is recoverable from shape, so the boundary is drawn by origin instead, and
   * an app that declares nothing forfeits asset paths rather than risking one.
   */
  readonly assetOrigins?: readonly string[];
}

/** Same identifier shapes `templatizeRumViewName` collapses, reused for the asset class. */
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGITS = /^\d+$/;
const LONG_HEX = /^[0-9a-f]{16,}$/i;
const OPAQUE_ID = /^(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]{12,}$/;

function isIdentifierSegment(segment: string): boolean {
  return (
    UUID_SEGMENT.test(segment) ||
    ALL_DIGITS.test(segment) ||
    LONG_HEX.test(segment) ||
    OPAQUE_ID.test(segment)
  );
}

/** Synthetic base for a relative input. Never surfaces in a result — see `sanitizeUrl`. */
const SYNTHETIC_BASE = 'https://relative.invalid';

function sanitizeQuery(params: URLSearchParams): string {
  // Rebuilt through `URLSearchParams` so every key and value is re-encoded.
  // Reading a param DECODES it, so string concatenation would let an allowlisted
  // value smuggle a delimiter and become two params — `?tab=chat%26memberId%3D<id>`
  // reconstructs as `?tab=chat&memberId=<id>`, an unredacted parameter created by
  // the sanitiser itself.
  const out = new URLSearchParams();
  // `forEach` rather than `for…of`: this module is compiled directly by consumer
  // apps whose tsconfig target predates iterable `URLSearchParams`.
  params.forEach((value, key) => {
    // Names are a CLOSED SET, not a shape test. Redacting values alone left
    // `?<uuid>=x` as `?<uuid>=REDACTED`, leaking the identifier in the key; a
    // word-shape test then still admitted `?Jane_Doe=x`, where the name itself is
    // the PII. Characterising "safe-looking" names failed twice, so only names
    // known to be diagnostic survive and every other parameter is dropped whole.
    // The cost is losing `memberId=` as a signal that a call was member-filtered;
    // the route template already identifies the endpoint.
    if (!SAFE_QUERY_PARAMS.has(key)) return;
    out.append(key, isSafeQueryValue(key, value) ? value : REDACTED_QUERY);
  });
  const rendered = out.toString();
  return rendered ? `?${rendered}` : '';
}

/**
 * Identifier-shaped tokens that may appear *inside* a path segment rather than
 * as the whole segment — an asset filename like `hero-<uuid>.png` embeds one.
 * Segment-level collapsing alone leaves those intact, which is fail-open.
 *
 * Deliberately narrower than the whole-segment rules: a 12-char hex build hash
 * (`index-a1b2c3d4e5f6.js`) is not an identifier and keeping it preserves
 * per-build asset granularity, so the hex arm requires 16+ characters.
 */
const EMBEDDED_IDENTIFIER =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,}|\d{6,}/gi;

/**
 * Collapse identifiers in a path retained by the `asset` class.
 *
 * Bounded exception, not a general safety net: it applies only to resource types
 * positively known to carry static content, and a short non-UUID identifier
 * embedded in such a filename would still survive. No bundled or media asset
 * path is member-keyed today; anything member-keyed belongs on an `api`- or
 * `page`-classed field, where template matching is authoritative.
 */
/** May this parameter's value be kept verbatim? */
function isSafeQueryValue(key: string, value: string): boolean {
  const shape = SAFE_QUERY_VALUE.get(key);
  if (!shape) return false;
  if (!shape.test(value)) return false;
  // `EMBEDDED_IDENTIFIER` is a global regex; `test` advances `lastIndex`, so reset.
  EMBEDDED_IDENTIFIER.lastIndex = 0;
  return !EMBEDDED_IDENTIFIER.test(value);
}

function collapseIdentifierSegments(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) =>
      isIdentifierSegment(segment) ? ':id' : segment.replace(EMBEDDED_IDENTIFIER, ':id'),
    )
    .join('/');
}

/**
 * Reduce one URL to a form safe for telemetry.
 *
 * Never throws: every failure path yields the destructive result, because a
 * raising `beforeSend` hook can drop events or destabilise the SDK.
 *
 * A relative input yields a relative output. {@link SYNTHETIC_BASE} exists only
 * so a same-origin path can be parsed and matched; letting it surface would put
 * a fabricated origin into telemetry, which reads as a real one.
 */
export function sanitizeUrl(raw: string, urlClass: UrlClass, sets: UrlTemplateSets = {}): string {
  try {
    if (!raw) return REDACTED_URL;

    const wasRelative = !/^[a-z][a-z0-9+.-]*:/i.test(raw) && !raw.startsWith('//');
    let parsed: URL;
    try {
      parsed = new URL(raw, SYNTHETIC_BASE);
    } catch {
      return REDACTED_URL;
    }

    // Only http(s) carries a meaningful route. Anything else (`javascript:`,
    // `data:`, `blob:`) is destroyed rather than parsed for a path.
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return REDACTED_URL;

    // The fragment is dropped on every class: it can carry an identifier
    // (`#/members/<uuid>`) and is never part of a route template.
    const origin = wasRelative ? '' : parsed.origin;
    const query = sanitizeQuery(parsed.searchParams);

    if (urlClass === 'destructive') {
      return `${origin}/${REDACTED_URL}`;
    }

    if (urlClass === 'asset') {
      // A relative URL is same-origin by definition, so it is the app's own bundle.
      const fromAssetOrigin = wasRelative || (sets.assetOrigins?.includes(parsed.origin) ?? false);
      if (!fromAssetOrigin) return `${origin}/${REDACTED_URL}`;
      return `${origin}${collapseIdentifierSegments(parsed.pathname)}${query}`;
    }

    const index = urlClass === 'api' ? sets.api : sets.page;
    const template = index ? matchRouteTemplate(index, parsed.pathname) : null;
    if (template === null) {
      // Fail closed: an unmatched path is destroyed, never passed through and
      // never handed to a shape heuristic (which misses short ids like
      // `member-123`).
      return `${origin}/${REDACTED_URL}`;
    }
    return `${origin}${template}${query}`;
  } catch {
    return REDACTED_URL;
  }
}

/**
 * Datadog `resource.type` values that carry static content. Only these retain a
 * path under the `asset` class.
 *
 * Three values are deliberately absent. `document` is the page navigation — a
 * deep link into a member detail page makes the document's own URL the
 * member-keyed page URL, so it is classed as `page` and matched against the page
 * templates; leaving it here would fall back to segment shape alone, which
 * misses short ids. `other` and `native` are the SDK's unclassified buckets and
 * may hold API-shaped requests, so they take the destructive default. Retaining
 * a path is only ever correct for a type positively known to be static content.
 */
const STATIC_CONTENT_RESOURCE_TYPES: ReadonlySet<string> = new Set([
  'css',
  'js',
  'image',
  'font',
  'media',
]);

/** `resource.type` values whose URL addresses an API route. */
const API_RESOURCE_TYPES: ReadonlySet<string> = new Set(['xhr', 'fetch', 'beacon']);

/**
 * Field paths whose treatment is known, keyed by dotted path. Everything absent
 * from this map takes the destructive default when it looks like a URL — that is
 * what makes the walk safe against SDK drift, since a URL-bearing field added by
 * a future SDK version is destroyed rather than leaked.
 *
 * `resource.url` is resolved dynamically instead, from its sibling
 * `resource.type` (see {@link classForField}).
 */
const FIELD_CLASSES: ReadonlyMap<string, UrlClass | 'skip'> = new Map<string, UrlClass | 'skip'>([
  ['error.resource.url', 'api'],
  ['http.url', 'api'],
  ['view.url', 'page'],
  ['view.referrer', 'page'],
  ['long_task.source_url', 'asset'],
  ['view.performance.lcp.resource_url', 'asset'],
  // `view.name` is not a URL: it is produced by `startWebRumView` and has
  // already passed through `templatizeRumViewName`, its own dedicated sanitiser.
  // It may legitimately be a screen id (Atlas) or an already-templated path
  // (web); destroying it here would undo that sanitiser's work.
  ['view.name', 'skip'],
]);

/**
 * Does this string address a resource at all? Deliberately generous — a false
 * positive costs one destroyed diagnostic value, a false negative leaks an
 * identifier, so the asymmetry is resolved in favour of destruction.
 */
function looksLikeUrl(value: string): boolean {
  // A rooted path (which also covers the protocol-relative `//host/…` form)…
  if (value.startsWith('/')) return true;
  // …or any scheme-qualified value, including the single-slash and slashless
  // forms (`javascript:/…`, `mailto:…`, `tel:…`) that a `://` test misses and
  // that therefore bypassed the destructive default entirely.
  //
  // Whitespace is the discriminator against prose: `TypeError: cannot read …`
  // also matches `scheme:`, and destroying an error message would be
  // over-redaction with no privacy gain, whereas a URL never contains a space.
  return !/\s/.test(value) && /^[a-z][a-z0-9+.-]{0,31}:/i.test(value);
}

function classForField(
  fieldPath: string,
  parent: Record<string, unknown> | undefined,
): UrlClass | 'skip' | undefined {
  if (fieldPath === 'resource.url') {
    const type = typeof parent?.type === 'string' ? parent.type : '';
    if (API_RESOURCE_TYPES.has(type)) return 'api';
    if (STATIC_CONTENT_RESOURCE_TYPES.has(type)) return 'asset';
    // `document` -> page; `other` / `native` / anything unrecognised -> destructive.
    return type === 'document' ? 'page' : 'destructive';
  }
  return FIELD_CLASSES.get(fieldPath);
}

/**
 * Walk a telemetry event and sanitise every URL-bearing value, dispatching on
 * the field path being visited.
 *
 * Path-aware rather than value-only because `resource.url`'s correct treatment
 * depends on its **sibling** `resource.type` — an asset URL should keep its path
 * while an API URL must be templated, and the two are indistinguishable from the
 * string alone.
 *
 * Never throws: on any failure the original value is replaced with the
 * destructive placeholder rather than propagating out of a `beforeSend` hook.
 */
export function scrubUrlValues(
  value: unknown,
  sets: UrlTemplateSets = {},
  fieldPath = '',
  parent?: Record<string, unknown>,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  try {
    if (typeof value === 'string') {
      const cls = classForField(fieldPath, parent);
      if (cls === 'skip') return value;
      if (cls !== undefined) return sanitizeUrl(value, cls, sets);
      // Unrecognised field path: destroy anything that addresses a resource,
      // leave everything else alone.
      return looksLikeUrl(value) ? sanitizeUrl(value, 'destructive', sets) : value;
    }

    if (value === null || typeof value !== 'object') return value;
    if (seen.has(value)) return undefined;
    seen.add(value);

    if (Array.isArray(value)) {
      // An array index is not a field name, so entries inherit the parent path.
      return value.map((entry) => scrubUrlValues(entry, sets, fieldPath, parent, seen));
    }

    if (!isRecord(value)) return REDACTED_URL;
    const source = value;
    const out: Record<string, unknown> = {};
    // Keys are enumerated first and each value read inside its own guard. A
    // single throwing getter must not abandon the whole object: returning the
    // original would leave every sibling URL unsanitised, which is fail-open at
    // the one place that must not be.
    let keys: readonly string[];
    try {
      keys = Object.keys(source);
    } catch {
      return REDACTED_URL;
    }
    for (const key of keys) {
      const childPath = fieldPath ? `${fieldPath}.${key}` : key;
      try {
        out[key] = scrubUrlValues(source[key], sets, childPath, source, seen);
      } catch {
        out[key] = REDACTED_URL;
      }
    }
    return out;
  } catch {
    // Never hand back the input: for a string that means the placeholder, and for
    // anything else a placeholder too, so a caller that merges the result cannot
    // silently retain the unsanitised original.
    return REDACTED_URL;
  }
}
